import { useEffect, useMemo, useState } from 'react'
import { ArrowLeft, ArrowRight, Loader2, Lock, Palette, Plus, Trash2, X } from 'lucide-react'
import { toast } from 'sonner'
import { Trans, useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Loading } from '@/components/shared/feedback'
import { Switch } from '@/components/ui/misc'
import { apiError } from '@/lib/api-client'
import { i18n } from '@/lib/i18n'
import { cn, formatNumber } from '@/lib/utils'
import { PROTECTED_STAGE_KEYS, type StageField, type StageKind } from '@/lib/lifecycle-stage-cache'
import { BUILTIN_STAGE_FIELDS, MAX_STAGE_FIELDS, PROP_PREFIX, builtinField, isPropRef, propKeyOf } from '@/lib/stage-fields'
import { useLifecycleStages, useSaveLifecycleStages } from '@/hooks/use-lifecycle-stages'
import { useCdpProperties, useCreateProperty, useLifecyclePipeline } from '@/hooks/use-cdp'

/** Một bước trong luồng. `uid` chỉ để chọn / kéo thả trên giao diện (bước mới chưa có `key`). */
type Row = { uid: string; key?: string; label: string; color: string | null; kind: StageKind; fields: StageField[] }

/** Trần số giai đoạn: pipeline 5–9 bước là phổ biến; quá 12 thì dải bước, phễu báo cáo và thanh giai đoạn khó đọc. Tổ chức đã có nhiều hơn vẫn lưu được (chỉ không thêm nữa). */
const MAX_STAGES = 12
const SWATCHES = ['#64748b', '#3b82f6', '#06b6d4', '#10b981', '#84cc16', '#f59e0b', '#f97316', '#ef4444', '#ec4899', '#8b5cf6', '#6366f1', '#0ea5e9']
/** `label`/`hint` là getter đọc `i18n.t()` lúc dùng — KHÔNG spread `{...item}` ở cấp module (đóng băng giá trị). */
const KIND_OPTIONS: { kind: StageKind; label: string; hint: string }[] = [
  {
    kind: 'nurture',
    get label() { return i18n.t('settings:lifecycleStages.kinds.nurture.label') },
    get hint() { return i18n.t('settings:lifecycleStages.kinds.nurture.hint') },
  },
  {
    kind: 'won',
    get label() { return i18n.t('settings:lifecycleStages.kinds.won.label') },
    get hint() { return i18n.t('settings:lifecycleStages.kinds.won.hint') },
  },
  {
    kind: 'lost',
    get label() { return i18n.t('settings:lifecycleStages.kinds.lost.label') },
    get hint() { return i18n.t('settings:lifecycleStages.kinds.lost.hint') },
  },
]

let seq = 0
const newUid = () => `n${++seq}`

// Mũi tên của một bước: bước đầu phẳng bên trái, mọi bước (trừ cuối) nhọn bên phải, các bước sau lõm bên trái — như Lark Base.
const TIP = 14
function chevron(first: boolean, last: boolean): string {
  const left = first ? ['0 0', '0 100%'] : [`${TIP}px 50%`]
  const right = last ? ['100% 0', '100% 100%'] : [`calc(100% - ${TIP}px) 0`, `100% 50%`, `calc(100% - ${TIP}px) 100%`]
  const pts = first
    ? ['0 0', ...right, '0 100%']
    : ['0 0', ...right, '0 100%', ...left]
  return `polygon(${pts.join(',')})`
}

/** Cấu hình luồng giai đoạn khách hàng theo kiểu "Luồng" của Lark Base: dải bước + form chi tiết bước đang chọn. */
export function LifecycleStagesTab() {
  const { t } = useTranslation('settings')
  const { stages, isLoading } = useLifecycleStages()
  const pipeline = useLifecyclePipeline()
  const save = useSaveLifecycleStages()
  const [rows, setRows] = useState<Row[]>([])
  const [dirty, setDirty] = useState(false)
  const [selected, setSelected] = useState<string | null>(null)
  const [dragFrom, setDragFrom] = useState<number | null>(null)

  useEffect(() => {
    if (!dirty) {
      const next = stages.map((s) => ({ uid: s.key, key: s.key, label: s.label, color: s.color, kind: s.kind, fields: s.fields ?? [] }))
      setRows(next)
      setSelected((cur) => (cur && next.some((r) => r.uid === cur) ? cur : next[0]?.uid ?? null))
    }
  }, [stages, dirty])

  // Tổ chức đã lỡ có nhiều hơn trần (bản cũ cho 20) vẫn được giữ nguyên, chỉ không thêm được nữa.
  const maxStages = Math.max(MAX_STAGES, stages.length)
  const counts = useMemo(() => {
    const c: Record<string, number> = {}
    for (const r of pipeline.data ?? []) c[r.lifecycleStage] = (c[r.lifecycleStage] ?? 0) + (r._count?._all ?? 0)
    return c
  }, [pipeline.data])

  const change = (fn: (r: Row[]) => Row[]) => {
    setDirty(true)
    setRows(fn)
  }
  const patch = (uid: string, p: Partial<Row>) => change((r) => r.map((x) => (x.uid === uid ? { ...x, ...p } : x)))
  const insertAt = (index: number) => {
    if (rows.length >= maxStages) return toast.error(t('settings:lifecycleStages.maxStagesError', { max: maxStages }))
    const uid = newUid()
    change((r) => [...r.slice(0, index), { uid, label: '', color: '#3b82f6', kind: 'nurture', fields: [] }, ...r.slice(index)])
    setSelected(uid)
    // Ô tên có sẵn trong form → lấy focus ngay để gõ tên bước mới.
    setTimeout(() => document.getElementById('stage-name')?.focus(), 0)
  }
  const move = (from: number, to: number) => {
    // Bước đầu (khách mới vào đây) giữ nguyên vị trí số 1.
    if (from === to || from < 1 || to < 1 || to >= rows.length) return
    change((r) => {
      const n = [...r]
      const [it] = n.splice(from, 1)
      n.splice(to, 0, it)
      return n
    })
  }
  const remove = (uid: string) => {
    const i = rows.findIndex((r) => r.uid === uid)
    change((r) => r.filter((x) => x.uid !== uid))
    setSelected(rows[i - 1]?.uid ?? rows[i + 1]?.uid ?? null)
  }

  const onSave = () =>
    save.mutate(
      rows.map((r) => ({ key: r.key, label: r.label.trim(), color: r.color, kind: r.kind, fields: r.fields })),
      {
        onSuccess: () => {
          setDirty(false)
          toast.success(t('settings:lifecycleStages.saved'))
        },
        onError: (e) => toast.error(apiError(e)),
      },
    )

  if (isLoading) return <Loading label={t('settings:lifecycleStages.loading')} />

  const sel = rows.find((r) => r.uid === selected) ?? null
  const selIdx = sel ? rows.indexOf(sel) : -1
  const locked = !!sel?.key && PROTECTED_STAGE_KEYS.includes(sel.key)
  const selCount = sel?.key ? (counts[sel.key] ?? 0) : 0

  return (
    // min-w-0: là phần tử của lưới trong hộp thoại — không có thì dải luồng rộng đẩy cả hộp tràn ngang; chỉ dải luồng được tự cuộn.
    <div className="min-w-0 space-y-4">
      <p className="max-w-3xl text-sm text-muted-foreground">
        <Trans t={t} i18nKey="settings:lifecycleStages.intro" components={{ b: <b /> }} />
      </p>

      {/* ── Dải bước ───────────────────────────────────────────── */}
      <div className="w-full max-w-full overflow-x-auto overflow-y-hidden rounded-xl bg-muted/40 px-4 pb-6 pt-10">
        <div className="flex min-w-max items-stretch">
          {rows.map((r, i) => {
            const first = i === 0
            const last = i === rows.length - 1
            const on = r.uid === selected
            const color = r.color ?? '#64748b'
            return (
              <div
                key={r.uid}
                className={cn('group relative', !first && '-ml-[10px]')}
                draggable={!first}
                onDragStart={() => setDragFrom(i)}
                onDragOver={(e) => dragFrom !== null && !first && e.preventDefault()}
                onDrop={() => {
                  if (dragFrom !== null) move(dragFrom, i)
                  setDragFrom(null)
                }}
                onDragEnd={() => setDragFrom(null)}
              >
                {/* Nút + chèn bước vào TRƯỚC bước này (không chèn trước bước đầu) */}
                {!first && rows.length < maxStages && (
                  <button
                    type="button"
                    aria-label={t('settings:lifecycleStages.insertBeforeAria', { name: r.label || t('settings:lifecycleStages.newStageLower') })}
                    title={t('settings:lifecycleStages.addStage')}
                    onClick={() => insertAt(i)}
                    className="absolute -top-7 left-[2px] z-10 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground opacity-0 shadow transition-opacity focus:opacity-100 group-hover:opacity-100"
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                )}
                {/* Viền chọn: lớp ngoài tô màu primary, nút bên trong chừa 2px — cùng hình mũi tên */}
                <div
                  style={{ clipPath: chevron(first, last), backgroundColor: on ? 'hsl(var(--primary))' : 'transparent' }}
                  className={cn('flex h-11 min-w-[150px]', dragFrom === i && 'opacity-50')}
                >
                  <button
                    type="button"
                    onClick={() => setSelected(r.uid)}
                    style={{
                      clipPath: chevron(first, last),
                      backgroundColor: `color-mix(in srgb, ${color} 22%, hsl(var(--background)))`,
                    }}
                    className="m-[2px] flex flex-1 items-center justify-center gap-2 px-6 text-sm font-medium"
                  >
                    <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: color }} />
                    <span className="max-w-[140px] truncate">{r.label || t('settings:lifecycleStages.newStage')}</span>
                    {r.kind === 'won' && <span className="rounded bg-emerald-500/15 px-1 text-[10px] text-emerald-600">{t('settings:lifecycleStages.badges.won')}</span>}
                    {r.kind === 'lost' && <span className="rounded bg-red-500/15 px-1 text-[10px] text-red-600">{t('settings:lifecycleStages.badges.lost')}</span>}
                  </button>
                </div>
              </div>
            )
          })}
          {/* + cuối pipeline (ẩn khi đã đủ số giai đoạn tối đa) */}
          {rows.length < maxStages && <button
            type="button"
            aria-label={t('settings:lifecycleStages.addAtEnd')}
            title={t('settings:lifecycleStages.addAtEnd')}
            onClick={() => insertAt(rows.length)}
            className="ml-3 flex h-11 w-11 shrink-0 items-center justify-center self-center rounded-full border border-dashed text-muted-foreground hover:border-primary hover:text-primary"
          >
            <Plus className="h-4 w-4" />
          </button>}
          {rows.length >= maxStages && <span className="ml-3 self-center whitespace-nowrap text-xs text-muted-foreground">{t('settings:lifecycleStages.maxStagesReached', { max: maxStages })}</span>}
        </div>
      </div>

      {/* ── Chi tiết bước đang chọn ────────────────────────────── */}
      {sel ? (
        <div className="grid gap-x-6 gap-y-4 sm:grid-cols-[200px_1fr]">
          <label htmlFor="stage-name" className="pt-2.5 text-sm">
            {t('settings:lifecycleStages.form.name')} <span className="text-destructive">*</span>
          </label>
          <div className="relative">
            <Input
              id="stage-name"
              value={sel.label}
              maxLength={40}
              onChange={(e) => patch(sel.uid, { label: e.target.value })}
              placeholder={t('settings:lifecycleStages.form.namePlaceholder')}
              className="h-11 pr-14"
            />
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  aria-label={t('settings:lifecycleStages.form.pickColorAria')}
                  title={t('settings:lifecycleStages.form.pickColor')}
                  style={{ backgroundColor: `color-mix(in srgb, ${sel.color ?? '#64748b'} 25%, hsl(var(--background)))` }}
                  className="absolute right-1.5 top-1.5 flex h-8 w-9 items-center justify-center rounded-md"
                >
                  <Palette className="h-4 w-4" style={{ color: sel.color ?? '#64748b' }} />
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-60 space-y-3">
                <div className="grid grid-cols-6 gap-2">
                  {SWATCHES.map((c) => (
                    <button
                      key={c}
                      type="button"
                      aria-label={c}
                      onClick={() => patch(sel.uid, { color: c })}
                      style={{ backgroundColor: c }}
                      className={cn('h-7 w-7 rounded-full border-2', sel.color === c ? 'border-foreground' : 'border-transparent')}
                    />
                  ))}
                </div>
                <label className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                  {t('settings:lifecycleStages.form.customColor')}
                  <input
                    type="color"
                    aria-label={t('settings:lifecycleStages.form.customColor')}
                    value={sel.color ?? '#64748b'}
                    onChange={(e) => patch(sel.uid, { color: e.target.value })}
                    className="h-8 w-12 cursor-pointer rounded border bg-transparent p-0.5"
                  />
                </label>
              </PopoverContent>
            </Popover>
          </div>

          <span className="pt-2.5 text-sm">
            {t('settings:lifecycleStages.form.kind')} <span className="text-xs text-muted-foreground">{t('settings:lifecycleStages.form.optional')}</span>
          </span>
          <div>
            {locked ? (
              <div
                className="inline-flex h-11 items-center gap-2 rounded-md border bg-muted/50 px-3 text-sm text-muted-foreground"
                title={t('settings:lifecycleStages.form.lockedTitle')}
              >
                <Lock className="h-4 w-4" /> {t('settings:lifecycleStages.form.lockedText')}
              </div>
            ) : (
              <Select value={sel.kind} onValueChange={(v) => patch(sel.uid, { kind: v as StageKind })}>
                <SelectTrigger className="h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {KIND_OPTIONS.map((o) => (
                    <SelectItem key={o.kind} value={o.kind}>
                      {o.label} <span className="text-xs text-muted-foreground">— {o.hint}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <span className="pt-2.5 text-sm">{t('settings:lifecycleStages.form.fields')}</span>
          <StageFieldsEditor fields={sel.fields} onChange={(fields) => patch(sel.uid, { fields })} />

          <span className="pt-1.5 text-sm">{t('settings:lifecycleStages.form.position')}</span>
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" variant="outline" size="sm" disabled={selIdx < 2} onClick={() => move(selIdx, selIdx - 1)}>
              <ArrowLeft className="h-4 w-4" /> {t('settings:lifecycleStages.form.moveLeft')}
            </Button>
            <Button type="button" variant="outline" size="sm" disabled={selIdx < 1 || selIdx >= rows.length - 1} onClick={() => move(selIdx, selIdx + 1)}>
              {t('settings:lifecycleStages.form.moveRight')} <ArrowRight className="h-4 w-4" />
            </Button>
            <span className="text-xs tabular-nums text-muted-foreground">
              {sel.key
                ? t('settings:lifecycleStages.form.customersHere', { count: selCount, total: formatNumber(selCount) })
                : t('settings:lifecycleStages.form.newNoCustomers')}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="ml-auto text-destructive"
              disabled={locked || rows.length <= 1 || selCount > 0}
              title={
                locked
                  ? t('settings:lifecycleStages.form.deleteLocked')
                  : selCount > 0
                    ? t('settings:lifecycleStages.form.deleteHasCustomers', { count: selCount })
                    : t('settings:lifecycleStages.form.delete')
              }
              onClick={() => remove(sel.uid)}
            >
              <Trash2 className="h-4 w-4" /> {t('settings:lifecycleStages.form.delete')}
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">{t('settings:lifecycleStages.selectPrompt')}</p>
      )}

      <div className="flex flex-wrap items-center justify-end gap-2 border-t pt-4">
        {dirty && (
          <Button type="button" variant="ghost" onClick={() => setDirty(false)}>
            {t('settings:lifecycleStages.undo')}
          </Button>
        )}
        <Button type="button" disabled={!dirty || save.isPending || rows.some((r) => !r.label.trim())} onClick={onSave}>
          {t('settings:lifecycleStages.savePipeline')}
        </Button>
      </div>

      <ul className="max-w-3xl list-disc space-y-1 pl-5 text-xs text-muted-foreground">
        <li><Trans t={t} i18nKey="settings:lifecycleStages.notes.firstStage" components={{ b: <b /> }} /></li>
        <li><Trans t={t} i18nKey="settings:lifecycleStages.notes.kinds" components={{ b: <b /> }} /></li>
      </ul>
    </div>
  )
}

/** `label` là getter đọc `i18n.t()` lúc dùng — KHÔNG spread `{...item}` ở cấp module (đóng băng giá trị). */
const PROP_TYPES: { value: string; label: string }[] = [
  { value: 'text', get label() { return i18n.t('settings:lifecycleStages.propTypes.text') } },
  { value: 'number', get label() { return i18n.t('settings:lifecycleStages.propTypes.number') } },
  { value: 'date', get label() { return i18n.t('settings:lifecycleStages.propTypes.date') } },
  { value: 'boolean', get label() { return i18n.t('settings:lifecycleStages.propTypes.boolean') } },
]

/**
 * "Trường bổ sung" của một bước: khi đưa khách vào bước này, người dùng điền các trường dưới đây (bắt buộc hoặc không).
 * Nguồn trường: cột khách của hệ thống + thuộc tính khách hàng; thiếu thì tạo thuộc tính mới ngay tại đây.
 */
function StageFieldsEditor({ fields, onChange }: { fields: StageField[]; onChange: (f: StageField[]) => void }) {
  const { t } = useTranslation('settings')
  const props = useCdpProperties()
  const createProp = useCreateProperty()
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [type, setType] = useState('text')

  const propList = props.data?.properties ?? []
  const labelOf = (ref: string) =>
    isPropRef(ref) ? propList.find((p) => p.fieldKey === propKeyOf(ref))?.name ?? propKeyOf(ref) : builtinField(ref)?.label ?? ref
  const have = new Set(fields.map((f) => f.ref))
  const add = (ref: string) => {
    if (fields.length >= MAX_STAGE_FIELDS) return toast.error(t('settings:lifecycleStages.fields.maxError', { max: MAX_STAGE_FIELDS }))
    onChange([...fields, { ref, required: false }])
    setOpen(false)
  }
  const onCreate = () =>
    createProp.mutate(
      { name: name.trim(), fieldType: type },
      {
        onSuccess: (p) => {
          toast.success(t('settings:lifecycleStages.fields.propCreated', { name: p.name }))
          setName('')
          add(`${PROP_PREFIX}${p.fieldKey}`)
        },
        onError: (e) => toast.error(apiError(e)),
      },
    )

  return (
    <div className="space-y-3">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" className="inline-flex items-center gap-1.5 pt-2 text-sm text-primary hover:underline">
            <Plus className="h-4 w-4" /> {t('settings:lifecycleStages.fields.add')}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-80 space-y-3">
          <div>
            <p className="mb-1 text-xs font-medium text-muted-foreground">{t('settings:lifecycleStages.fields.customerFields')}</p>
            <div className="max-h-48 space-y-0.5 overflow-y-auto">
              {BUILTIN_STAGE_FIELDS.filter((f) => !have.has(f.ref)).map((f) => (
                <button key={f.ref} type="button" onClick={() => add(f.ref)} className="block w-full rounded px-2 py-1.5 text-left text-sm hover:bg-muted">
                  {f.label}
                </button>
              ))}
              {propList.filter((p) => !have.has(`${PROP_PREFIX}${p.fieldKey}`)).map((p) => (
                <button key={p.id} type="button" onClick={() => add(`${PROP_PREFIX}${p.fieldKey}`)} className="flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-sm hover:bg-muted">
                  {p.name} <span className="text-[10px] text-muted-foreground">{t('settings:lifecycleStages.fields.propertyBadge')}</span>
                </button>
              ))}
              {BUILTIN_STAGE_FIELDS.every((f) => have.has(f.ref)) && propList.every((p) => have.has(`${PROP_PREFIX}${p.fieldKey}`)) && (
                <p className="px-2 py-1.5 text-xs text-muted-foreground">{t('settings:lifecycleStages.fields.allAdded')}</p>
              )}
            </div>
          </div>
          <div className="space-y-2 border-t pt-3">
            <p className="text-xs font-medium text-muted-foreground">{t('settings:lifecycleStages.fields.createHeading')}</p>
            <Input value={name} maxLength={60} onChange={(e) => setName(e.target.value)} placeholder={t('settings:lifecycleStages.fields.createPlaceholder')} />
            <div className="flex gap-2">
              <Select value={type} onValueChange={setType}>
                <SelectTrigger className="flex-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PROP_TYPES.map((pt) => <SelectItem key={pt.value} value={pt.value}>{pt.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button type="button" size="sm" disabled={!name.trim() || createProp.isPending} onClick={onCreate}>
                {createProp.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {t('settings:lifecycleStages.fields.createAndAdd')}
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {fields.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t('settings:lifecycleStages.fields.empty')}</p>
      ) : (
        <div>
          <div className="flex items-center justify-between border-b pb-1 text-xs text-muted-foreground">
            <span>{t('settings:lifecycleStages.fields.colField')}</span>
            <span className="mr-8">{t('settings:lifecycleStages.fields.colRequired')}</span>
          </div>
          {fields.map((f) => (
            <div key={f.ref} className="flex items-center gap-2 border-b border-dashed py-2 last:border-0">
              <span className="min-w-0 flex-1 truncate text-sm">{labelOf(f.ref)}</span>
              {isPropRef(f.ref) && <span className="text-[10px] text-muted-foreground">{t('settings:lifecycleStages.fields.propertyBadge')}</span>}
              <Switch
                checked={f.required}
                aria-label={t('settings:lifecycleStages.fields.requiredAria', { name: labelOf(f.ref) })}
                onCheckedChange={(v) => onChange(fields.map((x) => (x.ref === f.ref ? { ...x, required: v } : x)))}
              />
              <button type="button" aria-label={t('settings:lifecycleStages.fields.removeAria')} onClick={() => onChange(fields.filter((x) => x.ref !== f.ref))} className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-destructive">
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
          <p className="pt-1 text-[11px] text-muted-foreground">{t('settings:lifecycleStages.fields.footnote')}</p>
        </div>
      )}
    </div>
  )
}
